import { useParams, Navigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { blogPostsRu, getPostsForRegionRu } from "@/lib/blog-data-ru";
import { ALL_LOCATIONS } from "@/lib/locations";
import { ShareBarEs as ShareBarRu } from "@/components/ShareBarEs"; // reuse the logic
import { Calendar, ArrowRight } from "lucide-react";
import { PricingCalculator } from "@/components/PricingCalculator";
import { BlogLeadCapture } from "@/components/BlogLeadCapture";
import { CostOfSuppliesInfographic } from "@/components/CostOfSuppliesInfographic";
import { MaidServiceChecklistDownload } from "@/components/MaidServiceChecklistDownload";
import { MaidVsHouseCleaningInfographic } from "@/components/MaidVsHouseCleaningInfographic";
import { CleaningHabitTrackerDownload } from "@/components/CleaningHabitTrackerDownload";
import { StandardVsDeepSlider } from "@/components/StandardVsDeepSlider";
import { CleaningFrequencyPlanner } from "@/components/CleaningFrequencyPlanner";
import { BlogFAQ } from "@/components/BlogFAQ";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

const parseRussianDate = (dateString: string): string => {
  try {
    const months: Record<string, string> = {
      "января": "01",
      "февраля": "02",
      "марта": "03",
      "апреля": "04",
      "мая": "05",
      "июня": "06",
      "июля": "07",
      "августа": "08",
      "сентября": "09",
      "октября": "10",
      "ноября": "11",
      "декабря": "12"
    };

    const parts = dateString.trim().split(/\s+/);
    if (parts.length >= 3) {
      let day = parts[0];
      const monthName = parts[1].toLowerCase();
      const year = parts[2].replace(/[^0-9]/g, '');

      if (day.length === 1) day = "0" + day;
      const month = months[monthName];

      if (month && year.length === 4) {
        const isoString = `${year}-${month}-${day}T00:00:00.000Z`;
        const d = new Date(isoString);
        if (!isNaN(d.getTime())) {
          return d.toISOString();
        }
      }
    }
    
    const fallbackDate = new Date(dateString);
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate.toISOString();
    }
    
    return new Date().toISOString();
  } catch (e) {
    return new Date().toISOString();
  }
};

const RussianBlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  let post = blogPostsRu.find(p => p.slug === slug);
  if (!post) {
    for (const loc of ALL_LOCATIONS) {
      const localPosts = getPostsForRegionRu(loc.name, loc.regionSlug);
      const found = localPosts.find(p => p.slug === slug);
      if (found) {
        post = found;
        break;
      }
    }
  }

  if (!post) {
    return <Navigate to="/ru/blog" replace />;
  }

  const headings: { id: string; title: string }[] = [];
  const processedContent = post.content.replace(/<h2>(.*?)<\/h2>/g, (match, title) => {
    const cleanTitle = title.replace(/<[^>]+>/g, '').trim();
    const id = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ id, title: cleanTitle });
    return `<h2 id="${id}" class="scroll-m-24">${title}</h2>`;
  });

  return (
    <div className="flex min-h-screen flex-col" lang="ru">
      <SEO 
        title={`${post.title} | Uplevel Cleaning`}
        description={post.excerpt}
        url={`https://uplevelcleaning.com/ru/blog/${post.slug}`}
        alternateUrl={`https://uplevelcleaning.com/blog/${post.slug}`}
        alternateLang="en-US"
        lang="ru"
        articleData={{
          title: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: parseRussianDate(post.date),
          author: post.author
        }}
      />
      <Navbar />
      <main className="flex-1 pt-32 pb-24 relative">
        {showStickyCTA && (
          <div className="fixed top-0 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 md:hidden">
            <div className="container flex items-center justify-between">
              <span className="text-sm font-bold truncate mr-4">Готовы к чистому дому?</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap shrink-0">
                <a href="/book?view=custom-plan">Записаться</a>
              </Button>
            </div>
          </div>
        )}
        {showStickyCTA && (
          <div className="fixed top-20 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 hidden md:block">
            <div className="container flex items-center justify-between max-w-4xl">
              <span className="text-base font-bold">Готовы к чистому дому? Узнайте точную цену за 60 секунд.</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap">
                <a href="/book?view=custom-plan">Узнать цену и записаться</a>
              </Button>
            </div>
          </div>
        )}
        <article className="container max-w-4xl">
          <Link to="/ru/blog" className="text-primary hover:underline mb-8 inline-block">
            &larr; Назад в блог
          </Link>
          <header className="mb-12 text-center">
            <div className="text-sm text-primary font-bold uppercase tracking-wider mb-4">
              {post.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-muted-foreground text-sm">
              <span>{post.date}</span>
              <span>•</span>
              <span>{post.author}</span>
              <span>•</span>
              <span>{post.readTime}</span>
            </div>
          </header>
          <div className="aspect-video w-full rounded-2xl overflow-hidden mb-12 shadow-lg">
            <img src={post.image} alt={post.title} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col lg:flex-row gap-12 relative">
            <div className="hidden lg:block w-16 shrink-0">
              <div className="sticky top-32">
                <ShareBarRu url={`https://uplevelcleaning.com/ru/blog/${post.slug}`} title={post.title} variant="sticky-side" />
              </div>
            </div>
            <div className="flex-1">
              {headings.length > 0 && (
                <div className="bg-muted/30 p-6 rounded-2xl mb-12 border border-border">
                  <h3 className="font-bold text-lg mb-4">Оглавление</h3>
                  <ul className="space-y-3">
                    {headings.map(h => (
                      <li key={h.id}>
                        <a href={`#${h.id}`} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                          {h.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary hover:prose-a:underline" dangerouslySetInnerHTML={{ __html: processedContent }} />
              
              {(post.slug.includes("house-cleaning-cost") || post.slug.includes("pricing") || post.slug.includes("worth-it")) && (
                <CostOfSuppliesInfographic />
              )}

              {post.slug.includes("deep-cleaning-vs-standard-cleaning") && (
                <StandardVsDeepSlider />
              )}

              {post.slug.includes("maid-vs-house-cleaner") && (
                <MaidVsHouseCleaningInfographic />
              )}

              {post.slug.includes("keep-house-clean-between-cleanings") && (
                <CleaningHabitTrackerDownload />
              )}

              {post.slug.includes("how-often-should-you-clean-your-house") && (
                <div className="my-12">
                  <CleaningFrequencyPlanner />
                </div>
              )}

              {post.slug.includes("house-cleaning-cost") && (
                <div className="mt-12">
                  <PricingCalculator />
                  <BlogLeadCapture />
                </div>
              )}

              <BlogFAQ lang="ru" />

              <div className="mt-16 pt-12 border-t">
                <h3 className="text-2xl font-bold mb-8">Изучите наши услуги</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <Link to="/ru/limpieza-estandar" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                    <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Стандартная уборка</h4>
                    <p className="text-sm text-muted-foreground">Идеально для регулярного поддержания чистоты в доме.</p>
                  </Link>
                  <Link to="/ru/limpieza-profunda" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                    <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Генеральная уборка</h4>
                    <p className="text-sm text-muted-foreground">Детальная уборка сверху донизу для домов, требующих особого ухода.</p>
                  </Link>
                  <Link to="/ru/limpieza-mudanza" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                    <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Уборка при переезде</h4>
                    <p className="text-sm text-muted-foreground">Комплексная уборка пустых домов для возврата залога.</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Sticky Why Recurring Banner */}
        <div className="sticky bottom-6 mx-auto max-w-3xl z-40 px-4 pb-6 animate-in slide-in-from-bottom-10 fade-in duration-700">
          <Link to="/why-recurring" className="flex items-center justify-between bg-primary text-primary-foreground p-4 md:p-6 rounded-2xl shadow-2xl hover:bg-primary-hover transition-all group border-2 border-white/20">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full hidden sm:block">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg leading-tight">Хотите безупречный дом круглый год?</h4>
                <p className="text-primary-foreground/80 text-sm">Узнайте, почему регулярная уборка экономит до 35%.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-bold whitespace-nowrap">
              Подробнее <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RussianBlogPostPage;
